import type { Node } from 'reactflow';

type ParentedNode = Node & { parentNode?: string; parentId?: string };

const parentIdOf = (n: Node): string | undefined =>
  (n as ParentedNode).parentNode ?? (n as ParentedNode).parentId;

/**
 * Re-attach a group container's direct child to the canvas: strip
 * `parentNode`/`extent` and flatten its container-relative coordinates to
 * absolute ones. This is the detach the context-menu delete has always
 * performed — both delete paths share it so deleting a container keeps its
 * children at their absolute positions no matter how the delete was
 * triggered.
 */
export function detachChildFromGroup(child: Node, group: Node): Node {
  return {
    ...child,
    parentNode: undefined,
    extent: undefined,
    position: {
      x: group.position.x + child.position.x,
      y: group.position.y + child.position.y,
    },
  };
}

/**
 * Detach the direct children of every removed group container. Given the
 * pre-deletion nodes and the ids of the containers being removed, returns the
 * node array with those children re-attached to the canvas (absolute
 * coordinates). Non-children and children of other containers are untouched.
 */
export function detachChildrenOfRemovedGroups(
  nodes: Node[],
  removedGroupIds: ReadonlySet<string>
): Node[] {
  if (removedGroupIds.size === 0) return nodes;
  const groupById = new Map(nodes.map((n) => [n.id, n]));
  return nodes.map((n) => {
    const parentId = parentIdOf(n);
    if (!parentId || !removedGroupIds.has(parentId)) return n;
    const group = groupById.get(parentId);
    if (!group) return n;
    return detachChildFromGroup(n, group);
  });
}

/**
 * Deletion plan for React Flow's native removal path (Delete/Backspace via
 * `deleteKeyCode`): React Flow schedules the selected nodes for removal and
 * cascades to every descendant of a deleted container, so children would be
 * deleted with — or orphaned by — the container going away.
 *
 * Given the pre-deletion nodes and the ids React Flow is about to remove,
 * returns the nodes that should survive, keyed by id. Parity with the
 * context-menu delete (`deleteSelectedNodes`): the containers themselves and
 * everything the user explicitly selected die; every other scheduled
 * descendant survives, direct children of removed containers re-attached to
 * the canvas with absolute coordinates. Deeper descendants keep their (still
 * valid) coordinates relative to their surviving parent.
 */
export function planGroupDetach(
  nodes: Node[],
  removingIds: ReadonlySet<string>
): Map<string, Node> {
  const plan = new Map<string, Node>();
  if (removingIds.size === 0) return plan;
  // Everything the user explicitly selected dies; a child is detached from its
  // parent group only when that parent actually dies — a surviving container
  // keeps its children (with their container-relative coordinates).
  const dying = new Set(
    nodes.filter((n) => n.selected && removingIds.has(n.id)).map((n) => n.id)
  );
  if (dying.size === 0) return plan;
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  for (const n of nodes) {
    if (!removingIds.has(n.id) || n.selected) continue;
    const parentId = parentIdOf(n);
    const parent = parentId ? nodeById.get(parentId) : undefined;
    plan.set(
      n.id,
      parent && parent.type === 'groupNode' && dying.has(parent.id)
        ? detachChildFromGroup(n, parent)
        : n
    );
  }
  return plan;
}
